import type { ParsedTimestamp } from './types'

/**
 * Parsing the assessment timestamp.
 *
 * The schema asks for ISO 8601, but generators commonly write a naive local
 * timestamp with a space separator and microsecond precision, for example
 * "2026-07-02 17:14:27.703014". Passing that to the Date constructor is
 * unreliable: engines disagree on whether to accept it, so it cannot be trusted
 * across browsers.
 *
 * Both forms are handled explicitly. A value without a timezone is flagged
 * rather than silently assumed to be UTC, because assessments run in different
 * places and ordering them by a local wall clock is not sound.
 */

const ISO_WITH_ZONE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})$/
const NAIVE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?$/
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

export function parseTimestamp(input: unknown): ParsedTimestamp | undefined {
  if (typeof input !== 'string') return undefined
  const original = input.trim()
  if (original === '') return undefined

  const zoned = ISO_WITH_ZONE.exec(original)
  if (zoned) {
    // Millisecond precision is all Date carries; anything finer is dropped.
    // A zone written +0200 is not one Date.parse reliably reads; +02:00 is.
    const zone = zoned[8]!.replace(/^([+-]\d{2})(\d{2})$/, '$1:$2')
    const normalised = toIsoCandidate(zoned, zone)
    const value = Date.parse(normalised)
    if (Number.isNaN(value)) return undefined
    return { value, iso: new Date(value).toISOString(), naive: false, original }
  }

  const naive = NAIVE.exec(original)
  if (naive) {
    const value = Date.parse(toIsoCandidate(naive, ''))
    if (Number.isNaN(value)) return undefined
    return { value, iso: new Date(value).toISOString(), naive: true, original }
  }

  const dateOnly = DATE_ONLY.exec(original)
  if (dateOnly) {
    const value = Date.parse(`${original}T00:00:00`)
    if (Number.isNaN(value)) return undefined
    return { value, iso: new Date(value).toISOString(), naive: true, original }
  }

  return undefined
}

function toIsoCandidate(parts: RegExpExecArray, zone: string): string {
  const [, year, month, day, hour, minute, second = '00', fraction = ''] = parts
  const millis = fraction === '' ? '' : `.${fraction.slice(0, 3).padEnd(3, '0')}`
  return `${year}-${month}-${day}T${hour}:${minute}:${second}${millis}${zone}`
}

/**
 * The calendar day of an assessment, as its report wrote it.
 *
 * Not the day of the UTC instant: a zoneless "2026-07-01" is read as local
 * midnight, which in UTC is often the day before, and showing that would date
 * every such report a day early. The written day is also what a repository's
 * index records, so links and badges that name a day keep matching.
 */
export function dayOf(stamp: ParsedTimestamp | undefined): string | undefined {
  if (!stamp) return undefined
  return /^\d{4}-\d{2}-\d{2}/.exec(stamp.original)?.[0] ?? stamp.iso.slice(0, 10)
}

/** Date only, for axis labels and badges. */
export function formatDate(stamp: ParsedTimestamp | undefined): string {
  return dayOf(stamp) ?? 'unknown date'
}

/**
 * A version as people expect to read it: "v1.2" for a bare version number,
 * and as written otherwise, so "v.1.6" is not shown as "vv.1.6" and a commit
 * hash that happens to start with a digit is not dressed up as a release.
 */
export function formatVersion(version: string): string {
  return /^\d+(\.\d+)+/.test(version) ? `v${version}` : version
}
